<template>
    <app-framework>
      <header class="container">
        <bread-crumbs :list="[
          { name: $t('home'), link: '/' },
          { name: $t('reports_listing') },
          { name: $t('product_sales') }
        ]" />
        <h1>{{ $t('product_sales_report') }}</h1>
      </header>
      <article class="container framework-slot">
        <div class="form-row toolbar">
            <div class="col d-none d-md-block"></div>
            <div v-if="stores.nodes && stores.nodes.length" class="col col-12 col-sm-12 col-md-auto">
              <input-group>
                <select class="custom-select" :aria-label="$t('store')" v-model="form.storeId"
                        v-on:change="filter.storeId = form.storeId">
                  <option v-for="store in stores.nodes" :key="store.id" :value="store.id">{{store.name}}</option>
                </select>
              </input-group>
            </div>
            <div class="col col-12 col-sm-6 col-md-auto">
              <input-group>
                <select class="custom-select" :aria-label="$t('periodicity')" v-model="form.periodicity"
                        v-on:change="filter.periodicity = form.periodicity">
                  <option value="daily">{{ $t('daily') }}</option>
                </select>
              </input-group>
            </div>
            <div class="col col-12 col-sm-6 col-md-auto">
              <input-group>
                <input id="date" type="date" class="form-control" v-model="form.fromDay" :max="form.fromDayMax" v-on:change="filter.fromDay = form.fromDay" />
              </input-group>
            </div>
        </div>
        <div v-if="report.loading">
            <loading-state/>
        </div>
        <div v-else-if="report.error">
            <request-error-state :try-again-handler="refreshData" :message="report.error"/>
        </div>
        <div v-else-if="!report.data || !report.data.length">
            <empty-state/>
        </div>
        <div v-else>
            <table class="table">
                <thead>
                    <tr>
                        <th>Produto</th>
                        <th class="right">Qtd.</th>
                        <th class="right">Preço Médio</th>
                        <th class="right">Valor Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="reportData in report.data" :key="`report-data-${reportData.productId}`">
                        <td>
                            {{ reportData.productName }}
                        </td>
                        <td class="right">
                            {{ reportData.quantity }}
                        </td>
                        <td class="right">
                            {{ reportData.averageValue | formatCurrency }}
                        </td>
                        <td class="right">
                            {{ reportData.totalValue | formatCurrency }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
      </article>
    </app-framework>
</template>

<script>
    import AppFramework from "../../components/layout/AppFramework";
    import LoadingState from "../../components/states/LoadingState";
    import EmptyState from "../../components/states/EmptyState";
    import RequestErrorState from "../../components/states/RequestErrorState";
    import gql from "graphql-tag";
    import BreadCrumbs from "@/components/layout/BreadCrumbs";
    import InputGroup from '@/components/common/InputGroup.vue';
    import moment from "moment";

    export default {
        name: 'menuListing',
        i18nOptions: {namespaces: ['product_sales_report', 'common', 'breadcrumbs']},
        components: {BreadCrumbs, RequestErrorState, EmptyState, LoadingState, AppFramework, InputGroup},
        metaInfo() {
          return {
            title: this.$t('product_sales_report')
          }
        },
        data: () => ({
            filter: {
                storeId: null,
                periodicity: "daily",
                fromDay: moment().format("YYYY-MM-DD"),
                toDay: moment().format("YYYY-MM-DD")
            },
            form: {
                storeId: null,
                periodicity: "daily",
                fromDay: moment().format("YYYY-MM-DD"),
                toDay: moment().format("YYYY-MM-DD"),
                fromDayMax: moment().format("YYYY-MM-DD")
            },
            report: {
                data: null,
                loading: true,
                error: null,
                deletingMap: {},
                actionErrorMap: {}
            },
            selectedStore: null,
            stores: {
              nodes: null,
              loading: true,
              error: null
            }
        }),
        watch: {
            filter: {
                deep: true,
                async handler() {
                    if (this.filter.storeId) {
                        this.selectedStore = this.stores.nodes.find(n => n.id === this.filter.storeId)
                    }
                    await this.queryReport()
                }
            }
        },
        async mounted() {
            await this.queryStores()
            if (!this.filter.storeId) {
                if (this.stores.nodes && this.stores.nodes.length) {
                    this.filter.storeId = this.stores.nodes[0].id
                    this.form.storeId = this.stores.nodes[0].id
                    this.selectedStore = this.stores.nodes[0]
                }
            }
            await this.queryReport()
        },
        filters: {
          formatCurrency: function (number) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number)
          }
        },
        methods: {
            async queryStores(useCache = true) {
              this.stores.loading = true;
              try {
                const data = await this.$apolloClient.queryAll("stores", {
                  query: gql`query ($first: Int! $after: String) {
                      stores(first: $first after: $after) {
                          nodes {
                              id
                              name
                          }
                          pageInfo {
                              hasNextPage
                              endCursor
                          }
                      }
                  }`,
                  variables: {
                    first: 100
                  },
                  fetchPolicy: useCache ? "cache-first" : "network-only"
                });
                this.stores.nodes = data.nodes;
                this.stores.error = null;
              } catch (e) {
                if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                  if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                    this.stores.error = message;
                    return true;
                  }
                  return false;
                })) {
                  this.stores.error = "Error to load stores";
                }
              }
              this.stores.loading = false;
            },
            async queryReport(useCache = true) {
                this.report.deletingMap = {};
                this.report.actionErrorMap = {};
                this.report.loading = true;
                try {
                    const response = await this.$apolloClient.query({
                        query: gql`query ($storeId: ID! $fromDay: Day! $toDay: Day!) {
                            productSalesReport(
                                storeId: $storeId
                                fromDay: $fromDay
                                toDay: $toDay
                            ) {
                                productId
                                productName
                                quantity
                                averageValue
                                totalValue
                            }
                        }`,
                        variables: {
                            storeId: this.filter.storeId,
                            fromDay: this.filter.fromDay,
                            toDay: this.filter.fromDay
                        },
                        fetchPolicy: useCache ? "cache-first" : "network-only"
                    });
                    this.report.data = response.data.productSalesReport;
                    this.report.error = null;
                } catch (e) {
                    if (!e.graphQLErrors || !e.graphQLErrors.some(({extensions, message}) => {
                        if (extensions && extensions.code === "SHOW_ERROR_TO_USER") {
                            this.report.error = message;
                            return true;
                        }
                        return false;
                    })) {
                        this.report.error = "Error to load the report";
                    }
                }
                this.report.loading = false;
            },
            async refreshData() {
                await Promise.all([this.queryReport(false)]);
            }
        }
    }
</script>

<style scoped>
    .right {
        text-align: right;
    }
</style>